import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState } from "react";
import { Breadcrumb, Button, Modal } from "react-bootstrap";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";

import { useAppDispatch, useAppSelector } from "../../app/hook";
import {
  ChatbotRTKService,
  useAddFileKbAssistantBotMutation,
  useCreateAssistantBotMutation,
  useCreateKbAssistantBotMutation,
  useDeleteFileKbAssistantBotMutation,
  useEditAssistantBotMutation,
  useEditKbAssistantBotMutation,
  useGetAssistantsQuery,
  useLazyGetAllScenariosQuery,
  useSetDefaultScenariosMutation
} from "../../app/services/chatbot-rtk.services";
import {
  useCreateNewScenarioMutation,
  useModifyScenarioMutation
} from "../../app/services/scenario-rtk.service";
import { store } from "../../app/store";
import BotTypedVideoTutorial from "../../components/dashboard/knowledge-bases-create/BotTypedVideoTutorial";
import LouiseButton from "../../components/ui/LouiseButton";
import { AlertsType } from "../../constants/alerts.constant";
import { BotCategoryEnum } from "../../utils/BotCategoryEnum";
import { BotTypeEnum } from "../../utils/BotTypeEnum";
import { selectClient } from "../dashboard.slice";
import { setCurrentScenario } from "../scenario.slice";
import AiAdditionalInfo from "./AiAdditionalInfo";
import AssistantTemplates from "./AssistantTemplates";
import BotEngineSelector from "./BotEngineSelector";
import {
  assistantSelectors,
  handleInitializeEditWizard,
  handleResetWizard,
  handleUpdateCurrentStep,
  wizardState
} from "./botSlice";
import CloneExtraDescription from "./CloneExtraDescription";
import CloneExtraVideo from "./CloneExtraVideo";
import KnowledgeSeeder from "./KnowledgeSeeder";
import PlugAndPlayAutoUpdate from "./PlugAndPlayAutoUpdate";
import PlugAndPlayDomain from "./PlugAndPlayDomain";
import PlugAndPlayDomainLinks from "./PlugAndPlayDomainLinks";
import ReactionSpeed from "./ReactionSpeed";
import TextScenarioTemplates from "./TextScenarioTemplates";
import {
  AIKittyType,
  BotCode,
  CLONE_01_UI,
  KITTY_02_UI,
  KITTY_03_UI,
  KITTY_04_UI,
  MCBOT_01_UI,
  McBotFaceType,
  VIDEO_SCENARIO_UI
} from "./types";
import VideoScenarioTemplates from "./VideoScenarioTemplates";

export const ConnectForm = ({ children }: { children: any }) => {
  const methods = useFormContext();

  return children({ ...methods });
};

const BotWizard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const wizard = useAppSelector(wizardState);
  const client = useAppSelector(selectClient);

  const targetAssistantData = assistantSelectors.selectById(
    store.getState(),
    (wizard?.bot_config as AIKittyType)?.assistant
  );

  const [clonedData, setClonedData] = useState<any | null>(null);
  const [wizardStep, setWizardStep] = useState(
    wizard.bot_ui_info.currentStep || 1
  );

  // Initialize required data for clone (We have omitted required step in wizard so we have to insure that additional_instructions are prepared
  const { isError: isGetAssistantError, refetch: getAssistantRefetch } =
    useGetAssistantsQuery(
      {
        language: i18n.language
      },
      { skip: wizard.bot_code !== BotTypeEnum.HUMAN_CLONE }
    );

  // Getting all bots to check for their limit
  const [getAllBots] = useLazyGetAllScenariosQuery();

  // McBotType Mutators
  const [createMcBot, createMcBotResults] = useCreateNewScenarioMutation();
  const [editMcBot, editMcBotResults] = useModifyScenarioMutation();

  // AiKittyType Mutators
  const [createAiKittyBot, createAiKittyBotResults] =
    useCreateAssistantBotMutation();
  const [editAiKittyBot, editAiKittyBotResults] = useEditAssistantBotMutation();

  const [createKbAiKittyBot, createKbAiKittyBotResults] =
    useCreateKbAssistantBotMutation();
  const [editKbAiKittyBot, editKbAiKittyBotResults] =
    useEditKbAssistantBotMutation();
  const [, addFileKbAiKittyBotResults] = useAddFileKbAssistantBotMutation({
    fixedCacheKey: "aiKnowledgeFileMutator"
  });
  const [, removeFileKbAiKittyBotResults] = useDeleteFileKbAssistantBotMutation(
    { fixedCacheKey: "aiKnowledgeFileDeleteMutator" }
  );

  // Make default Mutator
  const [makeBotDefault, makeBotDefaultResults] =
    useSetDefaultScenariosMutation();

  const isApiInLoadingStatus =
    createMcBotResults.isLoading ||
    editMcBotResults.isLoading ||
    createAiKittyBotResults.isLoading ||
    editAiKittyBotResults.isLoading ||
    createKbAiKittyBotResults.isLoading ||
    editKbAiKittyBotResults.isLoading ||
    addFileKbAiKittyBotResults.isLoading ||
    removeFileKbAiKittyBotResults.isLoading ||
    makeBotDefaultResults.isLoading;

  const processExtraRequiredKeys = (data: any[] | undefined) => {
    const labelMapper: Record<string, string> = {
      company_name: t("Wizard/validationLabels/key8"),
      agent_name: t("Wizard/validationLabels/key9"),
      domain: t("Wizard/validationLabels/key10"),
      personality: t("Wizard/validationLabels/key11"),
      legal_sector: t("Wizard/validationLabels/key12"),
      business_sector: t("Wizard/validationLabels/key13")
    };

    if (data && data.length > 0) {
      const validationKeys: any = {};
      for (const obj of data) {
        if (obj.name == "agent_name") {
          validationKeys[obj.name] = Yup.string()
            .max(30, "Max length should be 30")
            .required()
            .label(labelMapper[obj.name]);
        } else {
          validationKeys[obj.name] = Yup.string()
            .required()
            .label(labelMapper[obj.name]); // Initialize or append
        }
      }
      return validationKeys;
    }
  };

  const cloneValidationSchema = () => {
    switch ((wizard.bot_config as AIKittyType)?.cloneExtra?.template) {
      case "basic":
        return {
          step3: Yup.object({
            model: Yup.string()
              .required()
              .label(t("Wizard/validationLabels/key5"))
          }),
          step4: Yup.object({
            training_data: Yup.array(
              Yup.mixed().required().label(t("Wizard/validationLabels/key6"))
            ).optional()
          })
        };
      case "clonePlusDescription":
        return {
          step3: Yup.object({
            name: Yup.string()
              .required()
              .label(t("Wizard/validationLabels/key18")),
            profession: Yup.string().label(t("Wizard/validationLabels/key19")),
            bio: Yup.string()
              .required()
              .label(t("Wizard/validationLabels/key20")),
            externalLinks: Yup.array(
              Yup.object({
                logo: Yup.string()
                  .required()
                  .label(t("Wizard/validationLabels/key21")),
                link: Yup.string()
                  .required()
                  .label(t("Wizard/validationLabels/key22"))
              }).required()
            )
          }),
          step4: Yup.object({
            model: Yup.string()
              .required()
              .label(t("Wizard/validationLabels/key5"))
          }),
          step5: Yup.object({
            training_data: Yup.array(
              Yup.mixed().required().label(t("Wizard/validationLabels/key6"))
            ).optional()
          })
        };
      case "clonePlusVideo":
        return {
          step3: Yup.object({
            videoBotId: Yup.string()
              .required()
              .label(t("Wizard/validationLabels/key23")),
            externalLinks: Yup.array(
              Yup.object({
                logo: Yup.string()
                  .required()
                  .label(t("Wizard/validationLabels/key21")),
                link: Yup.string()
                  .required()
                  .label(t("Wizard/validationLabels/key22"))
              }).required()
            )
          }),
          step4: Yup.object({
            model: Yup.string()
              .required()
              .label(t("Wizard/validationLabels/key5"))
          }),
          step5: Yup.object({
            training_data: Yup.array(
              Yup.mixed().required().label(t("Wizard/validationLabels/key6"))
            ).optional()
          })
        };
      default:
        return;
    }
  };

  const validationSchema: {
    [key: string]: {
      [key: string]: any;
    };
  } = {
    KITTY_02: {
      step2: Yup.object({
        assistant_type: Yup.string()
          .required()
          .label(t("Wizard/validationLabels/key1"))
      }),
      step3: Yup.object({
        additional_instructions: Yup.string()
          .optional()
          .label(t("Wizard/validationLabels/key2")),
        welcoming: Yup.string()
          .optional()
          .label(t("Wizard/validationLabels/key3")),
        ...processExtraRequiredKeys(targetAssistantData?.required_instructions)
      }),
      step4: Yup.object({
        model: Yup.string().required().label(t("Wizard/validationLabels/key5"))
      })
    },
    KITTY_03: {
      step2: Yup.object({
        assistant_type: Yup.string()
          .required()
          .label(t("Wizard/validationLabels/key1"))
      }),
      step3: Yup.object({
        additional_instructions: Yup.string()
          .optional()
          .label(t("Wizard/validationLabels/key2")),
        welcoming: Yup.string().optional(),
        ...processExtraRequiredKeys(targetAssistantData?.required_instructions)
      }),
      step4: Yup.object({
        training_data: Yup.array(
          Yup.mixed().required().label(t("Wizard/validationLabels/key4"))
        ).optional()
      }),
      step5: Yup.object({
        model: Yup.string().required().label(t("Wizard/validationLabels/key5"))
      })
    },
    KITTY_04: {
      step2: Yup.object({
        address: Yup.string()
          .matches(
            /^(www\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\.[a-zA-Z()]{2,6}\b([-a-zA-Z0-9()!@:%_\\+.~#?&\\/\\/=]*)/i,
            {
              message: t("DashboardSettingWidgetDefaultDomain/er-01")
            }
          )
          // .test("checkDomain", "Site map couldn't be found.", function (value) {
          //   if (
          //     value &&
          //     /^(www\.)?[-a-zA-Z0-9@:%._\\+~#=]{1,256}\.[a-zA-Z()]{2,6}\b([-a-zA-Z0-9()!@:%_\\+.~#?&\\/\\/=]*)/.test(
          //       value
          //     )
          //   )
          //     return new Promise((resolve, reject) => {
          //       HttpService.get(`/url-checker?url=https://${value}`, {
          //         headers: {
          //           "x-auth-token": getToken()
          //         }
          //       })
          //         .then(() => {
          //           // exists
          //           resolve(true);
          //         })
          //         .catch(() => {
          //           // note exists
          //           resolve(false);
          //         });
          //     });
          //   else return false;
          // })
          .required(),
        isVerified: Yup.bool()
          .oneOf([true], t("Wizard/PlugAndPlayInfos/key19"))
          .required(t("Wizard/PlugAndPlayInfos/key19"))
      }),
      step3: Yup.object({
        links: Yup.array()
          .of(Yup.string().url().required())
          .min(1)
          .max(20)
          .required()
      }),
      step4: Yup.object({
        auto_update: Yup.string()
          .required()
          .label(t("Wizard/validationLabels/key17"))
      })
    },
    MCBOT_01: {
      step2: Yup.object({
        template_id: Yup.number()
          .required()
          .label(t("Wizard/validationLabels/key6"))
      })
    },
    VIDEO_SCENARIO: {
      step2: Yup.object({
        template_id: Yup.number()
          .required()
          .label(t("Wizard/validationLabels/key6"))
      })
    },
    CLONE_01: {
      step2: Yup.object({
        welcoming: Yup.string()
          .optional()
          .label(t("Wizard/validationLabels/key3")),
        ...processExtraRequiredKeys(targetAssistantData?.required_instructions)
      }),
      ...cloneValidationSchema()
    }
  };

  const currentValidationSchema = wizard.bot_code
    ? validationSchema[wizard.bot_code][`step${wizardStep}`]
    : null;

  const methods = useForm({
    shouldUnregister: false,
    resolver: currentValidationSchema
      ? yupResolver(currentValidationSchema)
      : undefined,
    mode: "all"
  });

  const {
    handleSubmit,
    reset,
    trigger,
    formState: { errors }
  } = methods;

  const handleGoNextStep = async () => {
    if (wizardStep === 1 && step1IsValid()) {
      // The following lines check each bot type limit before create process starts
      if (!wizard.isInEditMode) {
        const botConfigs = client?.botConfigs;

        getAllBots({ botType: wizard.category === "clone" ? "link" : "widget" })
          .unwrap()
          .then((value) => {
            const targetBotCodeLimit = botConfigs?.find(
              (bot: any) => bot.botName === wizard.bot_code
            )?.config.limit;

            const totalOfTargetBotInList = value.filter(
              (item: any) => item.type === wizard.bot_code
            );

            if (
              targetBotCodeLimit &&
              totalOfTargetBotInList.length > 0 &&
              totalOfTargetBotInList.length >= targetBotCodeLimit
            ) {
              return toast.warn(t("General/key2"));
            } else {
              dispatch(handleUpdateCurrentStep(wizardStep + 1));
              return setWizardStep((prevWizardStep) => prevWizardStep + 1);
            }
          });
      } else {
        dispatch(handleUpdateCurrentStep(wizardStep + 1));
        return setWizardStep((prevWizardStep) => prevWizardStep + 1);
      }
    } else {
      const isStepValid = await trigger();
      if (isStepValid) {
        if (wizard.bot_type !== BotCategoryEnum.SCENARIO_BASED_CHATBOT) {
          dispatch(handleUpdateCurrentStep(wizardStep + 1));
          setWizardStep((prevWizardStep) => prevWizardStep + 1);
        }
        // Mock submit
        else onSubmit({});
      }
    }
  };

  const handleGoPrevStep = () => {
    if (wizardStep > 0) {
      dispatch(handleUpdateCurrentStep(wizardStep - 1));

      setWizardStep(wizardStep - 1);
    }
  };

  const handleCancelWizardProcess = () => {
    reset();

    setWizardStep(1);

    dispatch(handleResetWizard());
  };

  const step1IsValid = () => {
    if (wizard.bot_code === "") {
      return false;
    }

    if (!wizard.bot_config?.title || wizard.bot_config?.title === "") {
      return false;
    }

    if (
      !wizard.isInEditMode &&
      wizard.bot_code === BotTypeEnum.HUMAN_CLONE &&
      !(wizard.bot_config as AIKittyType).cloneExtra?.template
    )
      return false;

    return (
      wizard.bot_config?.title?.length >= 1 &&
      wizard.bot_config?.title?.length <= 30
    );
  };

  const breadcrumbRender = () => {
    switch (wizard.bot_code) {
      case BotTypeEnum.AI_READY_TO_USE:
        switch (wizardStep) {
          case 2:
            return (
              <Breadcrumb.Item active className="tw-whitespace-nowrap">
                {t(
                  (wizard.bot_ui_info.wizard as KITTY_02_UI)?.step2
                    .breadCrumbI18nKey
                )}
              </Breadcrumb.Item>
            );
          case 3:
            return (
              <React.Fragment>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as KITTY_02_UI)?.step2
                      .breadCrumbI18nKey
                  )}
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as KITTY_02_UI)?.step3
                      .breadCrumbI18nKey
                  )}
                </Breadcrumb.Item>
              </React.Fragment>
            );
          case 4:
            return (
              <React.Fragment>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as KITTY_02_UI)?.step2
                      .breadCrumbI18nKey
                  )}
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as KITTY_02_UI)?.step3
                      .breadCrumbI18nKey
                  )}
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as KITTY_02_UI)?.step4
                      .breadCrumbI18nKey
                  )}
                </Breadcrumb.Item>
              </React.Fragment>
            );
          default:
            return null;
        }
      case BotTypeEnum.AI_WITH_KNOWLEDGE_BASE:
        switch (wizardStep) {
          case 2:
            return (
              <Breadcrumb.Item active className="tw-whitespace-nowrap">
                {t(
                  (wizard.bot_ui_info.wizard as KITTY_03_UI)?.step2
                    .breadCrumbI18nKey
                )}
              </Breadcrumb.Item>
            );
          case 3:
            return (
              <React.Fragment>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as KITTY_03_UI)?.step2
                      .breadCrumbI18nKey
                  )}
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as KITTY_03_UI)?.step3
                      .breadCrumbI18nKey
                  )}
                </Breadcrumb.Item>
              </React.Fragment>
            );
          case 4:
            return (
              <React.Fragment>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as KITTY_03_UI)?.step2
                      .breadCrumbI18nKey
                  )}
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as KITTY_03_UI)?.step3
                      .breadCrumbI18nKey
                  )}
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as KITTY_03_UI)?.step4
                      .breadCrumbI18nKey
                  )}
                </Breadcrumb.Item>
              </React.Fragment>
            );
          case 5:
            return (
              <React.Fragment>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as KITTY_03_UI)?.step2
                      .breadCrumbI18nKey
                  )}
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as KITTY_03_UI)?.step3
                      .breadCrumbI18nKey
                  )}
                </Breadcrumb.Item>
                {wizard.isInEditMode && (
                  <Breadcrumb.Item active className="tw-whitespace-nowrap">
                    {t(
                      (wizard.bot_ui_info.wizard as KITTY_03_UI)?.step4
                        .breadCrumbI18nKey
                    )}
                  </Breadcrumb.Item>
                )}
                {(wizard.bot_ui_info.wizard as KITTY_03_UI)?.step5 && (
                  <Breadcrumb.Item active className="tw-whitespace-nowrap">
                    {t(
                      (wizard.bot_ui_info.wizard as KITTY_03_UI)?.step5
                        ?.breadCrumbI18nKey as string
                    )}
                  </Breadcrumb.Item>
                )}
              </React.Fragment>
            );
          default:
            return null;
        }
      case BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY:
        switch (wizardStep) {
          case 2:
            return (
              <Breadcrumb.Item active className="tw-whitespace-nowrap">
                {t(
                  (wizard.bot_ui_info.wizard as KITTY_04_UI)?.step2
                    .breadCrumbI18nKey as string
                )}
              </Breadcrumb.Item>
            );
          case 3:
            return (
              <React.Fragment>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as KITTY_04_UI)?.step2
                      .breadCrumbI18nKey as string
                  )}
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as KITTY_04_UI)?.step3
                      .breadCrumbI18nKey as string
                  )}
                </Breadcrumb.Item>
              </React.Fragment>
            );
          case 4:
            return (
              <React.Fragment>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as KITTY_04_UI)?.step2
                      .breadCrumbI18nKey as string
                  )}
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as KITTY_04_UI)?.step3
                      .breadCrumbI18nKey as string
                  )}
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as KITTY_04_UI)?.step4
                      .breadCrumbI18nKey as string
                  )}
                </Breadcrumb.Item>
              </React.Fragment>
            );
          default:
            return null;
        }
      case BotTypeEnum.TEXT_SCENARIO:
        switch (wizardStep) {
          case 2:
            return (
              <Breadcrumb.Item active className="tw-whitespace-nowrap">
                {t(
                  (wizard.bot_ui_info.wizard as MCBOT_01_UI)?.step2
                    .breadCrumbI18nKey
                )}
              </Breadcrumb.Item>
            );
          default:
            return null;
        }
      case BotTypeEnum.VIDEO_SCENARIO:
        switch (wizardStep) {
          case 2:
            return (
              <Breadcrumb.Item active className="tw-whitespace-nowrap">
                {t(
                  (wizard.bot_ui_info.wizard as VIDEO_SCENARIO_UI)?.step2
                    .breadCrumbI18nKey
                )}
              </Breadcrumb.Item>
            );
          default:
            return null;
        }
      case BotTypeEnum.HUMAN_CLONE:
        switch (wizardStep) {
          case 2:
            return (
              <Breadcrumb.Item active className="tw-whitespace-nowrap">
                {t(
                  (wizard.bot_ui_info.wizard as CLONE_01_UI)?.step2
                    .breadCrumbI18nKey
                )}
              </Breadcrumb.Item>
            );
          case 3:
            return (
              <React.Fragment>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as CLONE_01_UI)?.step2
                      .breadCrumbI18nKey
                  )}
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as CLONE_01_UI)?.step3
                      .breadCrumbI18nKey
                  )}
                </Breadcrumb.Item>
              </React.Fragment>
            );
          case 4:
            return (
              <React.Fragment>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as CLONE_01_UI)?.step2
                      .breadCrumbI18nKey
                  )}
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as CLONE_01_UI)?.step3
                      .breadCrumbI18nKey
                  )}
                </Breadcrumb.Item>
                {wizard.isInEditMode && (
                  <Breadcrumb.Item active className="tw-whitespace-nowrap">
                    {t(
                      (wizard.bot_ui_info.wizard as CLONE_01_UI)?.step4
                        ?.breadCrumbI18nKey as string
                    )}
                  </Breadcrumb.Item>
                )}
              </React.Fragment>
            );
          case 5:
            return (
              <React.Fragment>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as CLONE_01_UI)?.step2
                      .breadCrumbI18nKey
                  )}
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as CLONE_01_UI)?.step3
                      .breadCrumbI18nKey
                  )}
                </Breadcrumb.Item>
                <Breadcrumb.Item active className="tw-whitespace-nowrap">
                  {t(
                    (wizard.bot_ui_info.wizard as CLONE_01_UI)?.step4
                      ?.breadCrumbI18nKey as string
                  )}
                </Breadcrumb.Item>

                {wizard.isInEditMode && (
                  <Breadcrumb.Item active className="tw-whitespace-nowrap">
                    {t(
                      (wizard.bot_ui_info.wizard as CLONE_01_UI)?.step5
                        ?.breadCrumbI18nKey as string
                    )}
                  </Breadcrumb.Item>
                )}
              </React.Fragment>
            );
          default:
            return null;
        }
      default:
        return null;
    }
  };

  const stepsRender = () => {
    // This checking is for scenario when we want to create clone but api to fetch initial clone type additional structure fails
    if (
      wizard.bot_code &&
      wizard.bot_code === BotTypeEnum.HUMAN_CLONE &&
      isGetAssistantError
    )
      return (
        <div>
          {t("Wizard/general/key10")}
          <Button variant="link" onClick={getAssistantRefetch}>
            {t("Wizard/general/key11")}
          </Button>
        </div>
      );

    if (wizard.bot_code && step1IsValid() && wizardStep > 1)
      switch (wizard.bot_code) {
        case BotTypeEnum.AI_READY_TO_USE:
          switch (wizardStep) {
            case 2:
              return componentsMapper[
                `${(wizard.bot_ui_info.wizard as KITTY_02_UI)?.step2.component}`
              ];
            case 3:
              return componentsMapper[
                `${(wizard.bot_ui_info.wizard as KITTY_02_UI)?.step3.component}`
              ];
            case 4:
              return componentsMapper[
                `${(wizard.bot_ui_info.wizard as KITTY_02_UI)?.step4.component}`
              ];
            default:
              return null;
          }
        case BotTypeEnum.AI_WITH_KNOWLEDGE_BASE:
          switch (wizardStep) {
            case 2:
              return componentsMapper[
                `${(wizard.bot_ui_info.wizard as KITTY_03_UI)?.step2.component}`
              ];
            case 3:
              return componentsMapper[
                `${(wizard.bot_ui_info.wizard as KITTY_03_UI)?.step3.component}`
              ];
            case 4:
              return componentsMapper[
                `${(wizard.bot_ui_info.wizard as KITTY_03_UI)?.step4.component}`
              ];
            case 5:
              return componentsMapper[
                `${
                  (wizard.bot_ui_info.wizard as KITTY_03_UI)?.step5?.component
                }`
              ];
            default:
              return null;
          }
        case BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY:
          switch (wizardStep) {
            case 2:
              return componentsMapper[
                `${(wizard.bot_ui_info.wizard as KITTY_04_UI)?.step2.component}`
              ];
            case 3:
              return componentsMapper[
                `${(wizard.bot_ui_info.wizard as KITTY_04_UI)?.step3.component}`
              ];
            case 4:
              return componentsMapper[
                `${(wizard.bot_ui_info.wizard as KITTY_04_UI)?.step4.component}`
              ];
            default:
              return null;
          }
        case BotTypeEnum.TEXT_SCENARIO:
          switch (wizardStep) {
            case 2:
              return componentsMapper[
                `${(wizard.bot_ui_info.wizard as MCBOT_01_UI)?.step2.component}`
              ];
            default:
              return null;
          }
        case BotTypeEnum.VIDEO_SCENARIO:
          switch (wizardStep) {
            case 2:
              return componentsMapper[
                `${
                  (wizard.bot_ui_info.wizard as VIDEO_SCENARIO_UI)?.step2
                    .component
                }`
              ];
            default:
              return null;
          }
        case BotTypeEnum.HUMAN_CLONE:
          switch (wizardStep) {
            case 2:
              return componentsMapper[
                `${(wizard.bot_ui_info.wizard as CLONE_01_UI)?.step2.component}`
              ];
            case 3:
              return componentsMapper[
                `${(wizard.bot_ui_info.wizard as CLONE_01_UI)?.step3.component}`
              ];
            case 4:
              return componentsMapper[
                `${
                  (wizard.bot_ui_info.wizard as CLONE_01_UI)?.step4?.component
                }`
              ];
            case 5:
              return componentsMapper[
                `${
                  (wizard.bot_ui_info.wizard as CLONE_01_UI)?.step5?.component
                }`
              ];
            default:
              return null;
          }
        default:
          return null;
      }
    else return <BotEngineSelector />;
  };

  const componentsMapper: { [key: string]: React.ReactNode } = {
    TextScenarioTemplates: <TextScenarioTemplates />,
    AssistantTemplates: <AssistantTemplates />,
    AiAdditionalInfo: <AiAdditionalInfo />,
    ReactionSpeed: <ReactionSpeed />,
    KnowledgeSeeder: <KnowledgeSeeder />,
    VideoScenarioTemplates: <VideoScenarioTemplates />,
    PlugAndPlayDomain: <PlugAndPlayDomain />,
    PlugAndPlayDomainLinks: <PlugAndPlayDomainLinks />,
    PlugAndPlayAutoUpdate: <PlugAndPlayAutoUpdate />,
    CloneExtraDescription: <CloneExtraDescription />,
    CloneExtraVideo: <CloneExtraVideo />
  };

  /* Although data has all required data,
   * but in order to prevent multiple data source we will rely on state data
   */
  const onSubmit = (data: any) => {
    // Case for when data is not modified in order to prevent api call
    if (
      JSON.stringify(wizard?.bot_config) ===
      JSON.stringify(clonedData?.bot_config)
    ) {
      // Not sure maybe its better to show toas for real update??
      toast.success(t(AlertsType.ISKBKO004));

      if (wizard.bot_type === BotCategoryEnum.SCENARIO_BASED_CHATBOT) {
        dispatch(setCurrentScenario(wizard.id));

        handleCancelWizardProcess();

        return navigate(
          `chatbots/editor?type=${
            wizard.bot_code === BotTypeEnum.TEXT_SCENARIO
              ? "text-scenario"
              : "video-scenario"
          }`
        );
      }

      handleCancelWizardProcess();

      return;
    }

    if (wizard.bot_config)
      if (wizard.bot_type === BotCategoryEnum.SCENARIO_BASED_CHATBOT) {
        if (wizard.isInEditMode) {
          editMcBot({
            scenarioId: wizard.id,
            data: {
              title: (wizard.bot_config as McBotFaceType).title
            }
          })
            .unwrap()
            .then(() => {
              dispatch(setCurrentScenario(wizard.id));

              handleCancelWizardProcess();

              dispatch(
                ChatbotRTKService.util.invalidateTags([
                  "Package",
                  "GetAllScenarios"
                ])
              );

              navigate(
                `chatbots/editor?type=${
                  wizard.bot_code === BotTypeEnum.TEXT_SCENARIO
                    ? "text-scenario"
                    : "video-scenario"
                }`
              );
            });
        } else {
          createMcBot({
            title: (wizard.bot_config as McBotFaceType).title,
            bot_code: (wizard.bot_config as McBotFaceType).bot_code,
            template_id: (wizard.bot_config as McBotFaceType)
              .template_id as number
          })
            .unwrap()
            .then((values) => {
              dispatch(setCurrentScenario(values.id));

              handleCancelWizardProcess();

              dispatch(
                ChatbotRTKService.util.invalidateTags([
                  "Package",
                  "GetAllScenarios"
                ])
              );

              navigate(
                `chatbots/editor?type=${
                  wizard.bot_code === BotTypeEnum.TEXT_SCENARIO
                    ? "text-scenario"
                    : "video-scenario"
                }`
              );
            })
            .catch((err) => {
              toast.error(err.data?.message || err.message);
            });
        }
      } else {
        if (wizard.isInEditMode) {
          wizard.bot_code === BotTypeEnum.AI_WITH_KNOWLEDGE_BASE ||
          wizard.bot_code === BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY ||
          wizard.bot_code === BotTypeEnum.HUMAN_CLONE // if Ai is Knowledge then we have to manage files too
            ? editKbAiKittyBot({
                id: wizard.id,
                ...(wizard.bot_config as AIKittyType),
                model: (wizard.bot_config as AIKittyType).modelTitle
              })
                .unwrap()
                .then(() => {
                  toast.success(t(AlertsType.ISKBKO004));

                  handleCancelWizardProcess();

                  dispatch(
                    ChatbotRTKService.util.invalidateTags([
                      "Package",
                      "GetAllScenarios"
                    ])
                  );

                  navigate(
                    wizard.bot_code !== BotTypeEnum.HUMAN_CLONE
                      ? `chatbots`
                      : "human-clones"
                  );
                })
                .catch((err) => {
                  toast.error(err.data?.message || err.message);
                })
            : editAiKittyBot({
                id: wizard.id,
                ...(wizard.bot_config as AIKittyType)
              })
                .unwrap()
                .then(() => {
                  toast.success(t(AlertsType.ISKBKO004));

                  handleCancelWizardProcess();

                  dispatch(
                    ChatbotRTKService.util.invalidateTags([
                      "Package",
                      "GetAllScenarios"
                    ])
                  );

                  navigate(
                    wizard.bot_code !== BotTypeEnum.HUMAN_CLONE
                      ? `chatbots`
                      : "human-clones"
                  );
                })
                .catch((err) => {
                  toast.error(err.data?.message || err.message);
                });
        } else {
          wizard.bot_code === BotTypeEnum.AI_WITH_KNOWLEDGE_BASE ||
          wizard.bot_code === BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY ||
          wizard.bot_code === BotTypeEnum.HUMAN_CLONE // if Ai is Knowledge then we have to manage files too
            ? createKbAiKittyBot({
                ...(wizard.bot_config as AIKittyType),
                assistant:
                  (wizard.bot_config as AIKittyType).assistant ?? "free"
              })
                .unwrap()
                .then((data) => {
                  toast.success(t(AlertsType.ISKBKO008));

                  //This is for knowledge file uploader step after creating bot
                  if (wizard.bot_code !== BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY)
                    dispatch(
                      handleInitializeEditWizard({
                        id: data.id,
                        bot_code: data.type as BotCode,
                        bot_type:
                          (data.type as BotCode) ===
                            BotTypeEnum.VIDEO_SCENARIO ||
                          (data.type as BotCode) === BotTypeEnum.TEXT_SCENARIO
                            ? BotCategoryEnum.SCENARIO_BASED_CHATBOT
                            : BotCategoryEnum.AI_BASED_CHATBOT,
                        bot_config:
                          (data.type as BotCode) ===
                            BotTypeEnum.VIDEO_SCENARIO ||
                          (data.type as BotCode) === BotTypeEnum.TEXT_SCENARIO
                            ? ({
                                bot_code: data.type,
                                title: data.config.title,
                                template_id: 1
                              } as McBotFaceType)
                            : ({ ...data.config } as AIKittyType)
                      })
                    );
                })
                .then(() => {
                  if (wizard.bot_code !== BotTypeEnum.SITE_SCAN_PLUG_AND_PLAY)
                    handleGoNextStep();
                  else handleCancelWizardProcess();
                })
                .catch((err) => {
                  toast.error(err.data?.message || err.message);
                })
            : createAiKittyBot({ ...(wizard.bot_config as AIKittyType) })
                .unwrap()
                .then(() => {
                  toast.success(t(AlertsType.ISKBKO008));

                  handleCancelWizardProcess();

                  dispatch(
                    ChatbotRTKService.util.invalidateTags([
                      "Package",
                      "GetAllScenarios"
                    ])
                  );

                  navigate(
                    wizard.bot_code !== BotTypeEnum.HUMAN_CLONE
                      ? `chatbots`
                      : "human-clones"
                  );
                })
                .catch((err) => {
                  toast.error(err.data?.message || err.message);
                });
        }
      }
  };

  useEffect(() => {
    setClonedData(wizard);

    return () => {
      setClonedData(null);
    };
  }, [wizard.showModal]);

  useEffect(() => {
    return () => {
      handleCancelWizardProcess();
    };
  }, []);

  useEffect(() => {
    if (errors && Object.keys(errors).length > 0) {
      toast.warn(t("General/key14"));
    }
  }, [errors]);

  return (
    <Modal
      show={wizard.showModal}
      onHide={() => {
        if (!isApiInLoadingStatus) handleCancelWizardProcess();
      }}
      centered
      fullscreen
      className=""
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="modal-content">
          <Modal.Header
            closeButton
            className="tw-bg-gray-50 tw-border-gray-100 tw-text-gray-900 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
          >
            <ol className="breadcrumb tw-mb-0 tw-w-full tw-max-w-[95%] tw-overflow-auto tw-flex-nowrap [&>*]:tw-inline-flex">
              <Breadcrumb.Item active className="tw-whitespace-nowrap">
                {wizard.isInEditMode
                  ? t("Wizard/general/key1", {
                      title: wizard.bot_config?.title
                    })
                  : t("Wizard/general/key2")}{" "}
                {wizard.category === "widget"
                  ? t("Wizard/general/key3")
                  : t("Wizard/general/key4")}
              </Breadcrumb.Item>
              {breadcrumbRender()}
            </ol>
          </Modal.Header>
          <Modal.Body
            className="tw-bg-gray-50 tw-h-full"
            id="botWizardContainer"
          >
            {stepsRender()}
          </Modal.Body>
          <Modal.Footer
            className={`tw-bg-gray-50 tw-border-gray-100 md:tw-gap-0 tw-gap-14 tw-flex-row tw-items-end ${
              wizardStep == 1 &&
              wizard.bot_ui_info.videoTutorialLink.en.url &&
              wizard.bot_ui_info.videoTutorialLink.fr.url
                ? "tw-justify-between"
                : ""
            }`}
          >
            {wizardStep == 1 &&
              wizard.bot_ui_info.videoTutorialLink.en.url &&
              wizard.bot_ui_info.videoTutorialLink.fr.url && (
                <BotTypedVideoTutorial
                  en={wizard.bot_ui_info.videoTutorialLink.en}
                  fr={wizard.bot_ui_info.videoTutorialLink.fr}
                />
              )}

            {wizardStep != 1 && <div></div>}

            <div className="tw-flex tw-gap-3 md:tw-flex-nowrap tw-flex-wrap md:tw-w-max tw-justify-end">
              {wizardStep === 1 ? (
                <button
                  onClick={handleCancelWizardProcess}
                  type="button"
                  className="tw-border-0 tw-bg-transparent tw-text-blue-700 tw-px-5 tw-py-2"
                >
                  {t("Wizard/general/key5")}
                </button>
              ) : (
                <button
                  disabled={isApiInLoadingStatus}
                  onClick={() => handleGoPrevStep()}
                  type="button"
                  className={`${
                    wizard &&
                    wizard.bot_type &&
                    wizard.bot_type === BotCategoryEnum.AI_BASED_CHATBOT &&
                    wizard.bot_ui_info &&
                    wizard.bot_ui_info.wizard &&
                    wizardStep ===
                      Object.keys(wizard.bot_ui_info.wizard as any).length + 1
                      ? "md:tw-flex-none tw-flex-1"
                      : ""
                  } tw-border-0 tw-bg-transparent tw-text-blue-700 tw-px-5 tw-py-2`}
                >
                  {t("Wizard/general/key6")}
                </button>
              )}

              {wizard.bot_ui_info.wizard &&
              wizard.bot_type !== BotCategoryEnum.SCENARIO_BASED_CHATBOT &&
              wizardStep ===
                Object.keys(wizard.bot_ui_info.wizard as any).length + 1 ? (
                <React.Fragment>
                  <LouiseButton
                    variant="lightBlue"
                    type="submit"
                    disabled={isApiInLoadingStatus}
                    className="tw-rounded-full"
                  >
                    {t("Wizard/general/key7")}
                  </LouiseButton>
                </React.Fragment>
              ) : (
                <LouiseButton
                  variant="primary"
                  disabled={!step1IsValid() || isApiInLoadingStatus}
                  onClick={handleGoNextStep}
                  type="button"
                  className={`${
                    wizard &&
                    wizard.bot_type &&
                    wizard.bot_type === BotCategoryEnum.AI_BASED_CHATBOT &&
                    wizard.bot_ui_info &&
                    wizard.bot_ui_info.wizard &&
                    wizardStep ===
                      Object.keys(wizard.bot_ui_info.wizard as any).length + 1
                      ? "md:tw-flex-none tw-flex-1"
                      : ""
                  } tw-rounded-full`}
                >
                  {t("Wizard/general/key9")}
                </LouiseButton>
              )}
            </div>
          </Modal.Footer>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default BotWizard;
